class OlsunProductSearch extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.input = this.querySelector('input[name="q"]');
    this.results = this.querySelector('.och__search-results');
    this.status = this.querySelector('[data-search-status]');
    this.clearButton = this.querySelector('.och__search-clear');
    this.details = this.closest('details');
    this.panel = this.closest('.och__search-panel');
    this.cache ||= new Map();
    this.version = 0;
    this.activeIndex = -1;
    this.items = [];
    if (!this.input || !this.results || !this.status || !this.details) return;
    const options = { signal: this.controller.signal };
    this.input.addEventListener('input', (event) => { if (!event.isComposing) this.schedule(); }, options);
    this.input.addEventListener('compositionend', () => this.schedule(), options);
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim() && this.results.hidden) this.schedule();
    }, options);
    this.input.addEventListener('keydown', (event) => this.onKeyDown(event), options);
    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';
      this.schedule();
      this.input.focus();
    }, options);
    this.details.addEventListener('toggle', () => {
      if (this.details.open) {
        this.updateViewport();
        this.clearButton.hidden = !this.input.value.length;
      } else {
        this.cancelPending();
        this.resetResults();
        this.status.textContent = '';
      }
    }, options);
    window.addEventListener('resize', () => this.updateViewport(), options);
    window.visualViewport?.addEventListener('resize', () => this.updateViewport(), options);
    window.visualViewport?.addEventListener('scroll', () => this.updateViewport(), options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.cancelPending();
  }

  cancelPending() {
    this.version = (this.version || 0) + 1;
    clearTimeout(this.timer);
    clearTimeout(this.fetchTimeout);
    this.request?.abort();
  }

  resetResults() {
    this.items = [];
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.results.id);
    this.input.removeAttribute('aria-activedescendant');
    this.results.hidden = true;
    this.results.removeAttribute('aria-busy');
    this.results.replaceChildren();
  }

  schedule() {
    this.cancelPending();
    this.resetResults();
    this.clearButton.hidden = !this.input.value.length;
    this.status.textContent = '';
    const query = this.input.value.trim().slice(0, 128);
    if (!query || !this.details.open) return;
    const version = this.version;
    this.timer = setTimeout(() => this.load(query, version), 200);
  }

  async load(query, version) {
    if (version !== this.version || !this.details.open) return;
    const key = query.toLocaleLowerCase();
    this.status.textContent = 'Finding products…';
    this.results.setAttribute('aria-busy', 'true');
    const request = this.request = new AbortController();
    this.fetchTimeout = setTimeout(() => request.abort(), 4000);
    try {
      let markup = this.cache.get(key);
      if (!markup) {
        const url = new URL(this.dataset.predictiveUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', 'olsun-header-search-results');
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Suggestions unavailable');
        markup = await response.text();
      }
      if (version !== this.version || !this.details.open) return;
      if (request.signal.aborted) throw new Error('Suggestions timed out');
      const fragment = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-och-product-results]');
      if (!fragment) throw new Error('Missing suggestions');
      this.cache.set(key, markup);
      if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
      this.results.replaceChildren(...fragment.childNodes);
      const list = this.results.querySelector('[role="listbox"]');
      if (list) {
        list.id = `${this.results.id}-list`;
        this.input.setAttribute('aria-controls', list.id);
      }
      this.items = [...this.results.querySelectorAll('[data-och-result]')];
      this.items.forEach((item, index) => { item.id = `${this.results.id}-option-${index}`; });
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', String(this.items.length > 0));
      this.status.textContent = this.items.length
        ? `${this.items.length} product suggestion${this.items.length === 1 ? '' : 's'}.`
        : 'No products found. Try another product name.';
      this.updateViewport();
    } catch (_) {
      if (version !== this.version || !this.details.open) return;
      this.resetResults();
      this.status.textContent = 'Suggestions are unavailable. Use Search to see results.';
    } finally {
      if (version === this.version) {
        clearTimeout(this.fetchTimeout);
        this.results.removeAttribute('aria-busy');
      }
    }
  }

  onKeyDown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape' && (!this.results.hidden || this.status.textContent)) {
      event.preventDefault();
      this.cancelPending();
      this.resetResults();
      this.status.textContent = '';
      return;
    }
    if (!this.items.length || this.results.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = this.activeIndex < 0 ? (direction > 0 ? 0 : this.items.length - 1)
        : (this.activeIndex + direction + this.items.length) % this.items.length;
      this.items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.activeIndex)));
      const item = this.items[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', item.id);
      item.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.items[this.activeIndex].click();
    }
  }

  updateViewport() {
    if (!this.details?.open || !this.panel) return;
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.height + viewport.offsetTop : innerHeight;
    const available = Math.max(120, bottom - this.panel.getBoundingClientRect().top - 16);
    this.panel.style.setProperty('--och-search-max-height', `${Math.min(600, available)}px`);
  }
}

if (!customElements.get('olsun-product-search')) customElements.define('olsun-product-search', OlsunProductSearch);
